import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, CheckCircle2, XCircle, Loader2, Send, ShieldCheck, ExternalLink, RefreshCw } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

/** Google for Jobs — estado da Indexing API, sitemap e fila de notificações. */
export function AdminGoogleJobsCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = () => api.get("/admin/google-indexing").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/google-indexing/test");
      setTestResult(data);
      data.ok ? toast.success(data.message) : toast.error(data.message);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(""); }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/admin/google-indexing/process");
      toast.success(data.reason === "quota" ? "Quota diária já esgotada — continua amanhã." : `Enviadas ${data.sent} vagas ao Google (${data.errors} erros).`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(""); }
  };

  if (!st) return null;
  const pct = Math.min(100, Math.round((st.quota_used_today / Math.max(1, st.daily_quota)) * 100));

  return (
    <div data-testid="google-jobs-card" className="bg-white rounded-xl border border-black/5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <div className="font-heading font-bold tracking-tighter text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" /> Google for Jobs
            {st.configured ? (
              <Badge data-testid="google-jobs-status" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Indexing API ligada</Badge>
            ) : (
              <Badge data-testid="google-jobs-status" className="bg-amber-100 text-amber-800 hover:bg-amber-100">Falta a Service Account</Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Cada vaga tem dados estruturados <code className="text-xs">JobPosting</code> e entra no sitemap. Com a Indexing API,
            o Google é avisado de cada vaga nova (ou removida) — vagas de empresas primeiro, depois as importadas.
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="google-jobs-test-btn" variant="outline" className="rounded-full" disabled={!st.configured || !!busy} onClick={test}>
            {busy === "test" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />} Testar ligação
          </Button>
          <Button data-testid="google-jobs-send-btn" className="rounded-full bg-blue-600 hover:bg-blue-700" disabled={!st.configured || !!busy} onClick={sendNow}>
            {busy === "send" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Enviar agora
          </Button>
          <Button variant="outline" className="rounded-full" onClick={load} aria-label="Atualizar"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">Quota hoje</p>
          <p className="font-heading font-black text-xl text-slate-900" data-testid="google-jobs-quota">{st.quota_used_today}/{st.daily_quota}</p>
          <div className="h-1.5 rounded-full bg-slate-200 mt-1.5 overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">Notificadas</p>
          <p className="font-heading font-black text-xl text-emerald-700" data-testid="google-jobs-sent">{st.indexed_ok.toLocaleString("pt-PT")}</p>
          <p className="text-[11px] text-slate-500">de {st.active_jobs.toLocaleString("pt-PT")} vagas ativas</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">Em fila</p>
          <p className="font-heading font-black text-xl text-slate-900" data-testid="google-jobs-queued">{st.queued}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">Erros</p>
          <p className={`font-heading font-black text-xl ${st.errors ? "text-red-600" : "text-slate-900"}`}>{st.errors}</p>
        </div>
      </div>

      <div className="mt-4 text-xs space-y-1.5">
        <p className="flex flex-wrap items-center gap-1.5 text-slate-600">
          <span className="font-semibold">Sitemap:</span>
          <a data-testid="google-jobs-sitemap-link" href={st.sitemap_url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1 break-all">
            {st.sitemap_url} <ExternalLink className="w-3 h-3" />
          </a>
          <span className="text-slate-400">— submete-o no Search Console → Sitemaps</span>
        </p>
        {st.client_email && (
          <p className="text-slate-600"><span className="font-semibold">Service Account:</span> <code>{st.client_email}</code> — tem de ser <b>Proprietário</b> no Search Console.</p>
        )}
        {!st.https_ok && (
          <p className="text-amber-700 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> PUBLIC_BASE_URL tem de ser https para o Google aceitar os URLs.</p>
        )}
        {testResult && (
          <p data-testid="google-jobs-test-result" className={`flex items-center gap-1 ${testResult.ok ? "text-emerald-700" : "text-red-600"}`}>
            {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {testResult.message}
          </p>
        )}
        {st.last_error?.error && (
          <p className="text-red-600">Último erro: {st.last_error.error}</p>
        )}
        {!st.configured && (
          <ol className="mt-2 list-decimal list-inside text-slate-600 space-y-0.5" data-testid="google-jobs-setup-steps">
            <li>Google Cloud Console → cria/escolhe um projeto → ativa a <b>Web Search Indexing API</b>.</li>
            <li>IAM → Service Accounts → cria uma conta → Keys → <b>Add key → JSON</b>.</li>
            <li>Search Console → a tua propriedade → Definições → Utilizadores → adiciona o <code>client_email</code> como <b>Proprietário</b>.</li>
            <li>Cola o conteúdo do ficheiro JSON em <b>Chaves API → Google Indexing API</b>.</li>
          </ol>
        )}
      </div>
    </div>
  );
}

export default AdminGoogleJobsCard;
